import { useEffect, useRef, useState } from "react";
import { useCreateOrderMutation } from "../../../../../../../../API/authApi";
import useDialogHooks from "../../../../../../../../Components/Dialog/dialog-hooks";
import { IReserveOrderProps } from "./tableReserveDialog";
import useLanguagesHooks from "../../../../../../../../Components/Languages/setLanguage-hooks";
import { useSmoothScroll, useToastify } from "../../../../../../../../Hooks/hooks";
import { useForm } from "react-hook-form";
import { ITableReserveForm } from "../../../../../../../../Interfaces/formValue-interfaces";
import moment from "moment";
import { ICartItem, IOrderParams, IReserveOrderData, OrderTypes } from "../../../../../../../../Interfaces/componentTypes";
import { VisitorPageContents } from "../../../../../visitorPage";
import { getTotalCartItemCount } from "../../../../../../../../Utilits/utilits";
import { INewOrder } from "../../../../../../../../Interfaces/request-interfaces";
import OrderSuccess from "../OrderSuccess/orderSuccess";
import SmsCodeDialog from "../SMSCode/smsCodeDialog";

const useTableReservation = (props: IReserveOrderProps) => {
    const { companyId, company_name, company_logo, company_contacts, isReserveForm, setIsReserveForm, cart, serviceFee, onCloseDialog, setDialog, setContentType, setMainCart } = props;
    const [sendOrder, { isLoading: newOrderLoading }] = useCreateOrderMutation();
    const [dialogCart, setDialogCart] = useState<[] | ICartItem[]>(cart);
    const user_id = localStorage.getItem('user_id');
    const phone = localStorage.getItem('phone');
    const { open, onOpen: onOpenNoteDialog, onClose: onCloseNoteDialog } = useDialogHooks();
    const dialogRef = useRef<HTMLDivElement>(null);
    const { t } = useLanguagesHooks();
    const smoothScrollTo = useSmoothScroll();
    const { showToastError } = useToastify();
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [dineInPrice, setDineInPrice] = useState<number>(0);
    const [showOrder, setShowOrder] = useState(false);
    const [showEmptyCart, setShowEmptyCart] = useState(false);
    const totalItemCount = getTotalCartItemCount(dialogCart);

    const {
        register,
        control,
        formState: { errors },
        getValues,
        setValue,
        watch,
        reset,
        setError,
        handleSubmit,
    } = useForm<ITableReserveForm>({
        mode: 'onChange',
        defaultValues: {
            persons_count: "",
            phone_number: phone ?? "",
            date: moment().format('YYYY-MM-DD'),
            time: undefined,
            notes: "",
            pre_order: false,
            dine_in: true
        },
    });

    useEffect(() => {
        if (isReserveForm) {
            reset({
                persons_count: isReserveForm.persons_count ?? "",
                phone_number: isReserveForm.phone_number || (phone ?? ""),
                date: isReserveForm.date || moment().format("YYYY-MM-DD"),
                time: isReserveForm.time,
                notes: isReserveForm.notes || "",
                pre_order: isReserveForm.pre_order ?? false,
                dine_in: isReserveForm.dine_in ?? true,
            });
        }
        if (isReserveForm?.pre_order) {
            setShowOrder(true)
        }
    }, [isReserveForm, reset, phone]);

    const preOrder = watch("pre_order");

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (name === "pre_order") {
                if (values.pre_order) {
                    setValue("dine_in", false);
                    setShowOrder(true)
                    requestAnimationFrame(() => {
                        smoothScrollTo(dialogRef.current, dialogRef.current!.scrollHeight, 1500);
                    });
                } else if (!values.pre_order && !values.dine_in) {
                    setValue("dine_in", true);
                }
            }

            if (name === "dine_in") {
                if (values.dine_in) {
                    setTimeout(() => {
                        setShowOrder(false)
                    }, 500);
                    requestAnimationFrame(() => {
                        smoothScrollTo(dialogRef.current, 0, 500);
                    });
                    setValue("pre_order", false);
                } else if (!values.dine_in && !values.pre_order) {
                    setValue("pre_order", true);
                    requestAnimationFrame(() => {
                        smoothScrollTo(dialogRef.current, dialogRef.current!.scrollHeight, 1500);
                    });
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [watch, setValue, smoothScrollTo]);

    useEffect(() => {
        calculateTotalPrice(dialogCart);
    }, [dialogCart]);

    const onOrder = async () => {
        const phoneNumber = getValues('phone_number').trim().replace(/\s+/g, '');
        const personsount = getValues('persons_count');
        if (phoneNumber.length < 12) {
            setError("phone_number", { message: "" });
            dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        if (+personsount < 1) {
            setError("persons_count", { message: "" });
            dialogRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        const data: IReserveOrderData = {
            cart: preOrder ? dialogCart : [],
            service_fee: preOrder ? serviceFee : undefined,
            total_cost: preOrder ? dineInPrice : undefined
        };

        const params: IOrderParams = {
            phone_number: phoneNumber,
            notes: getValues('notes'),
            date: getValues('date'),
            time: getValues('time'),
            persons_count: getValues('persons_count'),
            totalItems: preOrder ? totalItemCount : undefined,
            company_id: companyId,
            company_name: company_name,
            company_logo: company_logo,
            company_contacts: company_contacts
        };

        const payload: INewOrder = {
            company_id: companyId,
            order_type: OrderTypes.RESERVE,
            phone_number: phoneNumber,
            uuid: user_id,
            data: JSON.stringify(data),
            order_parameters: JSON.stringify(params)
        };

        try {
            const response = await sendOrder(payload).unwrap();
            if(preOrder && data.cart.length > 0){
                setMainCart([]);
            }
            setIsReserveForm(null);
            setDialog(
                <OrderSuccess
                    setContentType={setContentType}
                    onClose={onCloseDialog}
                />)
        } catch (error: any) {
            const status = error?.error;
            if (status === 419) {
                setDialog(
                    <SmsCodeDialog
                        data={payload}
                        setDialog={setDialog}
                        setMainCart={setMainCart}
                        onCloseDialog={onCloseDialog}
                        setContentType={setContentType}
                    />
                );

            } else {
                showToastError(t('Toast.Error.Common'));
            }
        }
    };


    const onGoToMenu = () => {
        const values = getValues();
        setIsReserveForm(values);
        setTimeout(() => {
            setContentType(VisitorPageContents.MENU);
            onCloseDialog();
        }, 0);
    };


    const onCloseReserveDialog = () => {
        setIsReserveForm(null);
        onCloseDialog();
    }

    const removeFromDialogCart = (itemId: number) => {
        setDialogCart((prevCart) => {
            const updatedCart = prevCart.filter((cartItem) => cartItem.id !== itemId);
            setDialogCart(updatedCart);
            return updatedCart;
        });
    };

    const calculateTotalPrice = (cart: ICartItem[]) => {
        let total = 0;
        for (const item of cart) {
            let itemTotal = 0;
            for (const option of item.option) {
                itemTotal += parseFloat(option.price) * option.count;
            }
            total += itemTotal;
        }
        if (typeof serviceFee === 'number') {
            let dinInCost = total;
            dinInCost += dinInCost * (serviceFee / 100);
            setDineInPrice(dinInCost);
        } else {
            setDineInPrice(total);
        }
        setTotalPrice(total);
    };

    useEffect(() => {
        setDialogCart(cart);
    }, [cart]);

    useEffect(() => {
        setMainCart(dialogCart);
        calculateTotalPrice(dialogCart);
        setShowEmptyCart(dialogCart.length === 0);
    }, [dialogCart]);

    const handleNote = (note: string) => {
        setValue('notes', note.trim() || undefined);
        onCloseNoteDialog();
    };

    const handleOptionCountChange = (itemId: number, optionId: number, countChange: number) => {
        setDialogCart((prevCart) => {
            const updatedCart = prevCart.map((cartItem) => {
                if (cartItem.id === itemId) {
                    return {
                        ...cartItem,
                        option: cartItem.option.map((opt) =>
                            opt.option_id === optionId
                                ? { ...opt, count: opt.count + countChange }
                                : opt
                        ),
                    };
                }
                return cartItem;
            });
            setDialogCart(updatedCart);
            return updatedCart;
        });
    };

    return {
        open,
        errors,
        control,
        preOrder,
        dialogRef,
        showOrder,
        dialogCart,
        totalPrice,
        dineInPrice,
        showEmptyCart,
        totalItemCount,
        newOrderLoading,
        onOrder,
        register,
        onGoToMenu,
        getValues,
        watch,
        handleNote,
        handleSubmit,
        onOpenNoteDialog,
        onCloseNoteDialog,
        calculateTotalPrice,
        removeFromDialogCart,
        onCloseReserveDialog,
        handleOptionCountChange,
    }

};

export default useTableReservation